<template>
    <div class="vacancies-list card">
        <div class="vacancies-header">
            <!--h2 class="vacancies-title">Event Vacancies</h2-->
            <div class="vacancies-actions">
                <el-button
                  v-if="isAdmin"
                  type="primary"
                  @click="onAddVacancy"
                  :disabled="showForm || saving"
                  :loading="showForm || saving"
                >
                  Add Vacancy
                </el-button>
            </div>
        </div>

        <!-- Vacancy form shown inline when adding/editing -->
        <vacancy-form
          v-if="showForm"
          :isAdmin="isAdmin"
          :event-id="eventId"
          :activity-id="activityId"
          :is-new="!editingVacancy"
          :vacancy="editingVacancy"
          @saved="saveVacancy"
          @close="onVacancyClose"
        />

        <div v-if="loading" class="vacancies-empty">Loading vacancies...</div>
        <div v-else-if="error" class="vacancies-empty">Failed to load vacancies.</div>
        <div v-else-if="vacancies.length === 0" class="vacancies-empty">No vacancies yet</div>

        <div class="event-table">
            <el-table v-if="vacancies?.length" :data="vacancies" style="width: 100%" empty-text="No vacancies">
                <!-- Title: remove fixed width so it fits content, show tooltip on overflow -->
                <el-table-column prop="title" label="Title" :min-width="120" show-overflow-tooltip>
                    <template #default="scope">
                        <el-link
                          type="primary"
                          :underline="false"
                          @click.prevent="editVacancy(scope.row)"
                          class="event-table__name"
                        >
                          {{ scope.row.title }}
                        </el-link>
                    </template>
                </el-table-column>

                <el-table-column prop="description" label="Description" :min-width="150">
                    <template #default="scope">
                    <div class="dash-if-empty">
                        {{ scope.row.description }}
                    </div>
                    </template>
                </el-table-column>
                <el-table-column prop="requiredLevel" label="Reqd Level" :min-width="100">
                    <template #default="scope">
                    <div>
                        {{ scope.row.requiredLevel }}
                    </div>
                    </template>
                </el-table-column>
                <el-table-column prop="status" label="Status" :min-width="120">
                    <template #default="scope">
                    <div class="dash-if-empty">
                        {{ scope.row.status }}
                    </div>
                    </template>
                </el-table-column>

                <!-- Created By and Updated By: fit to text -->
                <el-table-column v-if="isAdmin" prop="createdByUser" label="Created By" :min-width="140" show-overflow-tooltip>
                    <template #default="scope">
                    <div class="dash-if-empty">
                        {{ scope.row.createdByUser }}
                    </div>
                    </template>
                </el-table-column>

                <el-table-column v-if="isAdmin" prop="updatedByUser" label="Updated By" :min-width="140" show-overflow-tooltip>
                    <template #default="scope">
                    <div class="dash-if-empty">
                        {{ scope.row.updatedByUser }}
                    </div>
                    </template>
                </el-table-column>

                <!-- Last column (Delete or Apply): fixed width -->
                <el-table-column v-if="isAdmin" prop="delete" label="Delete" width="140">
                    <template #default="scope">
                        <el-button
                        class="activity-cv-block__remove-button"
                        type="primary"
                        @click="deleteVacancy(scope.row.id)"
                        >
                        <el-icon><Delete/></el-icon>
                        </el-button>   
                    </template>
                </el-table-column>

                <el-table-column v-else prop="apply" label="Apply" width="140">
                    <template #default="scope">
                      <div>
                        <el-button v-if="!scope.row.applicationStatus"
                                   class="activity-cv-block__remove-button"
                                   type="primary"
                                   @click="applyToVacancy(scope.row.id)"
                                   >
                          Apply
                        </el-button>
                        <div v-else class="dash-if-empty">
                          {{ scope.row.applicationStatus }}
                        </div>
                      </div>
                    </template>
                </el-table-column>
            </el-table>
        </div>  
    </div>
</template>

<script>
import { getVacancies } from '@/api/events.js';
import { addVacancy, updateVacancy, deleteVacancy as apiDeleteVacancy, applyToVacancy as apiApplyToVacancy } from '@/api/vacancies.js';
import VacancyForm from '@/components/vacancies/VacancyForm.vue';

export default {
    name: 'EventVacancies',
    components: {
      VacancyForm,
    },
    props: {
      eventId: {
        type: [String, Number],
        required: true,
      },
      isAdmin: {
        type: Boolean,
        default: false,
        required: false,
      },
      activityId: { 
        type: [Number],
        default: null,
      },
    },
    data() {
        return {
            vacancies: [],
            loading: false,
            error: null,
            showForm: false,
            saving: false,
            editingVacancy: null,
        };
    },
    created() {
        this.fetchVacancies();
    },
    methods: {
        async fetchVacancies() {
            console.log('Fetching vacancies for event', this.eventId);
            if (!this.eventId) return;
            this.loading = true;
            this.error = null;
            try {
                const res = await getVacancies(this.eventId, false, 0, 100);
                console.log('Vacancies response:', res);
                if( res.success ) {
                    this.vacancies = res.data?.items || [];
                } else {
                    this.error = res.message || 'Failed to load vacancies';
                    this.vacancies = [];
                }
            } catch (err) {
                console.error('Failed to fetch vacancies', err);
                this.error = err;
                this.vacancies = [];
            } finally {
                this.loading = false;
            }
        },
        onAddVacancy() {
          this.editingVacancy = null
          this.showForm = true
        },
        async saveVacancy(vacancyFormData) {
          // call add/update API depending on payload (existing logic kept)
          try {
            this.saving = true;
            // if vacancyFormData contains an id then it's an update; otherwise create
            if (vacancyFormData && vacancyFormData.id) {
              // update API not implemented here: reuse addVacancy or call update endpoint if exists
              await updateVacancy(vacancyFormData);
            } else {
              await addVacancy(vacancyFormData);
            }
          } catch (err) {
            console.error('saveVacancy error', err);
          } finally {
            this.saving = false;
            this.showForm = false;
            this.editingVacancy = null;
            await this.fetchVacancies();
          }
        },
        onVacancyClose() {
          this.showForm = false
          this.editingVacancy = null
        },

        // open form for editing the selected vacancy
        editVacancy(vacancy) {
          this.editingVacancy = vacancy ? { ...vacancy } : null
          this.showForm = true
        },

        // new: ask for confirmation and call API delete
        async deleteVacancy(id) {
          if (!id) return;
          // confirmation (Element UI / Element Plus)
          let confirmed = false;
          if (this.$confirm && typeof this.$confirm === 'function') {
            try {
              await this.$confirm(
                'Are you sure you want to delete this vacancy?',
                'Confirm delete',
                { confirmButtonText: 'Delete', cancelButtonText: 'Cancel', type: 'warning' }
              );
              confirmed = true;
            } catch (e) {
              // user cancelled
              return;
            }
          } else {
            // fallback to window.confirm
            if (!window.confirm('Are you sure you want to delete this vacancy?')) return;
            confirmed = true;
          }

          if (!confirmed) return;

          this.saving = true;
          try {
            const res = await apiDeleteVacancy(id);
            if (res && res.success) {
              if (this.$message && typeof this.$message === 'function') {
                this.$message({ type: 'success', message: 'Vacancy deleted' });
              } else {
                console.info('Vacancy deleted');
              }
              await this.fetchVacancies();
            } else {
              const msg = (res && (res.message || res.error)) || 'Failed to delete vacancy';
              if (this.$message && typeof this.$message === 'function') {
                this.$message({ type: 'error', message: msg });
              } else {
                console.error(msg);
              }
            }
          } catch (err) {
            console.error('deleteVacancy error', err);
            if (this.$message && typeof this.$message === 'function') {
              this.$message({ type: 'error', message: 'Error deleting vacancy' });
            }
          } finally {
            this.saving = false;
          }
        },

        // show prompt for cover letter, call API only when user confirms
        async applyToVacancy(id) {
          if (!id) return;

          let coverLetter = null;

          // Prefer Element's prompt if available
          if (this.$prompt && typeof this.$prompt === 'function') {
            try {
              // Element (or Element Plus) $prompt returns a promise that resolves when confirmed
              const res = await this.$prompt(
                'Please enter a cover letter to apply for this vacancy:',
                'Apply to vacancy',
                {
                  confirmButtonText: 'Apply',
                  cancelButtonText: 'Cancel',
                  inputType: 'textarea',
                  inputPlaceholder: 'Write your cover letter here...',
                }
              );
              // Element's prompt usually resolves with an object containing `value`
              coverLetter = res?.value ?? res;
            } catch (e) {
              // user cancelled -> do nothing
              return;
            }
          } else {
            // fallback to window.prompt
            const result = window.prompt('Enter cover letter to apply (Cancel to abort):', '');
            if (result === null) return; // cancelled
            coverLetter = result;
          }

          // call API
          this.saving = true;
          try {
            const res = await apiApplyToVacancy(id, coverLetter);
            if (res && res.success) {
              if (this.$message && typeof this.$message === 'function') {
                this.$message({ type: 'success', message: 'Application submitted' });
              }
            } else {
              const msg = (res && (res.message || res.error)) || 'Failed to submit application';
              if (this.$message && typeof this.$message === 'function') {
                this.$message({ type: 'error', message: msg });
              } else {
                console.error(msg);
              }
            }
          } catch (err) {
            console.error('applyToVacancy error', err);
            if (this.$message && typeof this.$message === 'function') {
              this.$message({ type: 'error', message: 'Error submitting application' });
            }
          } finally {
            this.saving = false;
            await this.fetchVacancies();
          }
        },
    }
};
</script>

<style lang="scss">

.event-table {
  &__buttons {
    text-align: right;
  }

  &__action {
    padding: var(--global-dimension-size-75) 0;
    cursor: pointer;
    font-weight: 500;

    &:hover {
      color: var(--color-primary-400);
    }
  }

  &__actions-menu {}

  &__actions {
    margin-left: var(--global-dimension-size-125);
  }
}

.vacancies-list {
    padding: 16px;
}

/* header layout similar to EventApplications */
.vacancies-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.vacancies-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
}

.vacancies-actions {
    display: flex;
    gap: 8px;
}

/* table styling */
.vacancies-table {
    width: 100%;
    border-collapse: collapse;
    background: transparent;
}

.vacancies-table thead th {
    text-align: left;
    padding: 10px;
    border-bottom: 1px solid #e6e6e6;
    background: #fafafa;
    font-weight: 600;
}

.vacancies-table td {
    padding: 10px;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: top;
}

.skills-list {
    margin: 0;
    padding-left: 16px;
}

.vacancies-empty {
    padding: 20px 0;
    color: #666;
    text-align: center;
}
</style>